import React from 'react';

/**
 * Status Badge Component
 * Displays colored status badges for leads and tasks
 *
 * @param {string} status - Status text
 * @param {string} color - Hex color code (optional, will use default colors if not provided)
 * @param {string} size - Badge size: 'sm', 'md', 'lg'
 */
const StatusBadge = ({ status, color = null, size = 'md' }) => {
    // Default status colors (can be overridden by color prop)
    const statusColors = {
        // Lead statuses
        'customer': '#7cb342',
        'contacted': '#42a5f5',
        'qualified': '#66bb6a',
        'unqualified': '#ef5350',
        'lost': '#9e9e9e',
        'junk': '#757575',

        // Task statuses
        'not started': '#9e9e9e',
        'in progress': '#42a5f5',
        'testing': '#ffa726',
        'awaiting feedback': '#ab47bc',
        'complete': '#66bb6a',
        'completed': '#66bb6a',

        // Generic statuses
        'active': '#66bb6a',
        'inactive': '#9e9e9e',
        'pending': '#ffa726',
        'approved': '#7cb342',
        'rejected': '#ef5350'
    };

    // Size configurations
    const sizeClasses = {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-3 py-1 text-sm',
        lg: 'px-4 py-1.5 text-base'
    };

    // Get color from prop or default mapping
    const badgeColor = color || statusColors[status.toLowerCase()] || '#9e9e9e';

    // Convert hex to RGB for background with opacity
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16)
              }
            : { r: 0, g: 0, b: 0 };
    };

    const rgb = hexToRgb(badgeColor);
    const bgColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`;

    return (
        <span
            className={`inline-flex items-center rounded-full font-semibold ${sizeClasses[size]}`}
            style={{
                backgroundColor: bgColor,
                color: badgeColor,
                border: `1px solid ${badgeColor}`
            }}
        >
            {status}
        </span>
    );
};

/**
 * Priority Badge Component
 * Specialized badge for task priorities
 *
 * @param {number|string} priority - Priority level (1=Low, 2=Medium, 3=High, 4=Urgent)
 */
export const PriorityBadge = ({ priority }) => {
    const priorityMap = {
        1: { label: 'Low', color: '#9e9e9e' },
        2: { label: 'Medium', color: '#42a5f5' },
        3: { label: 'High', color: '#ffa726' },
        4: { label: 'Urgent', color: '#ef5350' },
        'low': { label: 'Low', color: '#9e9e9e' },
        'medium': { label: 'Medium', color: '#42a5f5' },
        'high': { label: 'High', color: '#ffa726' },
        'urgent': { label: 'Urgent', color: '#ef5350' }
    };

    const priorityConfig = priorityMap[priority] || priorityMap[2];

    return <StatusBadge status={priorityConfig.label} color={priorityConfig.color} size="sm" />;
};

export default StatusBadge;
